// src/modules/myvault/components/canvasBuilder/properties/ActionButtonProperties.tsx

// What? Properties panel for the `action_button` canvas element. MVP R-3a:
//       label, variant/colour, confirm-before-run, and a SEND_EMAIL direct
//       action with template picker + RecipientPicker wiring.
// Why?  The orphaned RecipientPicker component now has its first real
//       consumer; this panel is how admins configure what an action button
//       actually does. Future action types (UPDATE_FIELD, RUN_SKILL, etc.)
//       are intentionally disabled in the type selector with a "coming soon"
//       tooltip.
// How?  Reads/writes the element's open-ended `props` map. The `direct_action`
//       field is what the backend's CanvasService.execute_action_button
//       reads out of `config_json` at runtime; spreading props into
//       `config_json` on save is already handled by CanvasBuilder's save
//       path.

import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Option,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/joy";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import { useGroups } from "../../../hooks/useGroups";
import { useCanvases } from "../../../hooks/useCanvases";
import { useViews } from "../../../hooks/useViews";
import RecipientPicker, { RecipientEntry } from "../../RecipientPicker";
import type {
  CanvasElementProps,
  CanvasNavigationAction,
  CanvasNavigationConfig,
} from "../../../types";

type ActionVariant = "solid" | "outlined" | "soft";
type ActionColor = "primary" | "neutral" | "success" | "warning" | "danger";

interface TemplateSummary {
  template_key: string;
  module: string;
  display_name: string;
  subject_template: string;
  is_system: boolean;
}

interface DirectActionSendEmail {
  type: "SEND_EMAIL";
  config: {
    template?: string;
    recipients?: RecipientEntry[];
    cc?: RecipientEntry[];
  };
}

interface ActionButtonPropsShape extends CanvasElementProps {
  label?: string;
  actionVariant?: ActionVariant;
  actionColor?: ActionColor;
  confirm_before_run?: boolean;
  confirmation?: { enabled?: boolean; message?: string; title?: string };
  direct_action?: DirectActionSendEmail | CanvasNavigationAction;
}

interface ActionButtonPropertiesProps {
  workspaceId: string;
  props: ActionButtonPropsShape;
  onChange: (patch: Partial<ActionButtonPropsShape>) => void;
}

const VARIANTS: { value: ActionVariant; label: string }[] = [
  { value: "solid", label: "Solid" },
  { value: "outlined", label: "Outlined" },
  { value: "soft", label: "Soft" },
];

const COLORS: { value: ActionColor; label: string }[] = [
  { value: "primary", label: "Primary" },
  { value: "neutral", label: "Neutral" },
  { value: "success", label: "Success" },
  { value: "warning", label: "Warning" },
  { value: "danger", label: "Danger" },
];

const ActionButtonProperties: React.FC<ActionButtonPropertiesProps> = ({
  workspaceId,
  props,
  onChange,
}) => {
  const authFetch = useAuthFetch();
  const { groups } = useGroups(workspaceId);
  const { canvases } = useCanvases(workspaceId);
  const { views } = useViews(workspaceId);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [destinationType, setDestinationType] = useState<"canvas" | "view">(
    props.direct_action?.type === "NAVIGATE" &&
      props.direct_action.config.target_view_id
      ? "view"
      : "canvas",
  );

  useEffect(() => {
    if (props.direct_action?.type !== "NAVIGATE") return;
    if (props.direct_action.config.target_view_id) setDestinationType("view");
    if (props.direct_action.config.target_canvas_id)
      setDestinationType("canvas");
  }, [props.direct_action]);

  useEffect(() => {
    if (!workspaceId || props.direct_action?.type === "NAVIGATE") return;
    let cancelled = false;

    (async () => {
      setTemplatesLoading(true);
      setTemplatesError(null);
      try {
        const data = await authFetch(
          `/api/email/workspace/${workspaceId}/templates`,
          "fetch workspace templates",
        );
        if (!cancelled && Array.isArray(data)) setTemplates(data);
      } catch (err) {
        if (!cancelled) {
          setTemplatesError(
            err instanceof Error ? err.message : "Failed to load templates",
          );
        }
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authFetch, workspaceId, props.direct_action?.type]);

  const directAction: DirectActionSendEmail | CanvasNavigationAction =
    props.direct_action ?? {
      type: "SEND_EMAIL",
      config: {},
    };
  const actionConfig =
    directAction.type === "SEND_EMAIL" ? directAction.config : {};
  const navigationConfig: CanvasNavigationConfig =
    directAction.type === "NAVIGATE" ? directAction.config : {};
  const recipients: RecipientEntry[] = actionConfig.recipients ?? [];

  const updateDirectAction = (
    patch: Partial<DirectActionSendEmail["config"]>,
  ) => {
    onChange({
      direct_action: {
        type: "SEND_EMAIL",
        config: { ...actionConfig, ...patch },
      },
    });
  };

  const confirmEnabled = props.confirm_before_run ?? true;

  const updateNavigation = (patch: Partial<CanvasNavigationConfig>) => {
    onChange({
      direct_action: {
        type: "NAVIGATE",
        config: { ...navigationConfig, ...patch },
      },
    });
  };

  return (
    <Stack spacing={2}>
      <Typography level="title-sm">Button appearance</Typography>

      <Stack direction="row" spacing={2}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Variant</FormLabel>
          <Select
            size="sm"
            value={props.actionVariant ?? "solid"}
            onChange={(_, val) =>
              onChange({ actionVariant: (val as ActionVariant) || "solid" })
            }
          >
            {VARIANTS.map((v) => (
              <Option key={v.value} value={v.value}>
                {v.label}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Colour</FormLabel>
          <Select
            size="sm"
            value={props.actionColor ?? "primary"}
            onChange={(_, val) =>
              onChange({ actionColor: (val as ActionColor) || "primary" })
            }
          >
            {COLORS.map((c) => (
              <Option key={c.value} value={c.value}>
                {c.label}
              </Option>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <FormControl>
        <Stack direction="row" spacing={1} alignItems="center">
          <Switch
            size="sm"
            checked={confirmEnabled}
            onChange={(e) =>
              onChange({
                confirm_before_run: e.target.checked,
                confirmation: {
                  ...(props.confirmation ?? {}),
                  enabled: e.target.checked,
                },
              })
            }
          />
          <FormLabel sx={{ m: 0 }}>
            Ask for confirmation before running
          </FormLabel>
        </Stack>
        <FormHelperText>
          Shows a confirmation dialog when a viewer clicks the button.
        </FormHelperText>
      </FormControl>

      <Divider />
      <Typography level="title-sm">Action</Typography>

      <FormControl>
        <FormLabel>Action type</FormLabel>
        <Select
          size="sm"
          value={directAction.type}
          onChange={(_, value) => {
            if (value === "NAVIGATE") {
              onChange({
                confirm_before_run: false,
                confirmation: { ...(props.confirmation ?? {}), enabled: false },
                direct_action: {
                  type: "NAVIGATE",
                  config: { record_source: "none", return_to_source: true },
                },
              });
            } else if (value === "SEND_EMAIL") {
              onChange({ direct_action: { type: "SEND_EMAIL", config: {} } });
            }
          }}
        >
          <Option value="SEND_EMAIL">Send email</Option>
          <Option value="NAVIGATE">Open canvas or view</Option>
          <Tooltip title="Coming soon" placement="right">
            <Option value="UPDATE_FIELD" disabled>
              Update field
            </Option>
          </Tooltip>
          <Tooltip title="Coming soon" placement="right">
            <Option value="RUN_SKILL" disabled>
              Run skill
            </Option>
          </Tooltip>
          <Tooltip title="Coming soon" placement="right">
            <Option value="WEBHOOK" disabled>
              Webhook
            </Option>
          </Tooltip>
        </Select>
        <FormHelperText>
          Choose where this button takes the viewer.
        </FormHelperText>
      </FormControl>

      {directAction.type === "NAVIGATE" ? (
        <>
          <FormControl>
            <FormLabel>Destination type</FormLabel>
            <Select
              size="sm"
              value={destinationType}
              onChange={(_, value) => {
                setDestinationType(value === "view" ? "view" : "canvas");
                updateNavigation({
                  target_canvas_id: undefined,
                  target_view_id: undefined,
                });
              }}
            >
              <Option value="canvas">Canvas</Option>
              <Option value="view">View</Option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Destination</FormLabel>
            {destinationType === "view" ? (
              <Select
                size="sm"
                value={navigationConfig.target_view_id || null}
                onChange={(_, value) =>
                  updateNavigation({ target_view_id: value || undefined })
                }
              >
                {views.map((view) => (
                  <Option key={view.id} value={String(view.id)}>
                    {view.name}
                  </Option>
                ))}
              </Select>
            ) : (
              <Select
                size="sm"
                value={navigationConfig.target_canvas_id || null}
                onChange={(_, value) =>
                  updateNavigation({ target_canvas_id: value || undefined })
                }
              >
                {canvases.map((canvas) => (
                  <Option key={canvas.id} value={String(canvas.id)}>
                    {canvas.name}
                  </Option>
                ))}
              </Select>
            )}
            {(navigationConfig.target_canvas_id &&
              !canvases.some(
                (canvas) =>
                  String(canvas.id) === navigationConfig.target_canvas_id,
              )) ||
            (navigationConfig.target_view_id &&
              !views.some(
                (view) => String(view.id) === navigationConfig.target_view_id,
              )) ? (
              <FormHelperText>
                Selected destination is unavailable to you.
              </FormHelperText>
            ) : null}
          </FormControl>
          <FormControl>
            <FormLabel>Record to pass</FormLabel>
            <Select
              size="sm"
              value={navigationConfig.record_source || "none"}
              onChange={(_, value) =>
                updateNavigation({
                  record_source: (value ||
                    "none") as CanvasNavigationConfig["record_source"],
                })
              }
            >
              <Option value="none">No record</Option>
              <Option value="current">Current canvas record</Option>
              <Option value="selected">Selected grid row</Option>
            </Select>
          </FormControl>
          <FormControl>
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                size="sm"
                checked={navigationConfig.return_to_source !== false}
                onChange={(event) =>
                  updateNavigation({ return_to_source: event.target.checked })
                }
              />
              <FormLabel sx={{ m: 0 }}>Back returns to this canvas</FormLabel>
            </Stack>
          </FormControl>
        </>
      ) : (
        <>
          <FormControl>
            <FormLabel>Email template</FormLabel>
            <Select
              size="sm"
              value={actionConfig.template ?? ""}
              placeholder={
                templatesLoading ? "Loading templates..." : "Select a template"
              }
              onChange={(_, val) =>
                updateDirectAction({ template: (val as string) || undefined })
              }
              disabled={templatesLoading}
            >
              <Option value="">None</Option>
              {templates.map((t) => (
                <Option key={t.template_key} value={t.template_key}>
                  {t.display_name}
                </Option>
              ))}
            </Select>
            {templatesError && (
              <FormHelperText sx={{ color: "danger.600" }}>
                {templatesError}
              </FormHelperText>
            )}
            {!templatesError && (
              <FormHelperText>
                Templates come from your org&apos;s email configuration. Admins
                can edit them in Org Settings / Email Templates.
              </FormHelperText>
            )}
          </FormControl>

          <RecipientPicker
            value={recipients}
            onChange={(next) => updateDirectAction({ recipients: next })}
            groups={groups}
          />

          <Box sx={{ mt: 0.5 }}>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              Recipients are resolved when a viewer clicks the button. If the
              organization has the &quot;Restrict automations to org-verified
              domains&quot; lockdown enabled, recipients outside verified
              domains are filtered at send time.
            </Typography>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default ActionButtonProperties;
